import React from 'react';
import SubScore from "./SubScore";
import intToBool from "@Number/intToBool";
import formatDate from "@DateTime/functions/formatDate";
import IconThumb from "@Icons/IconThumb";
import ExpandableContainer from "@Containers/components/ExpandableContainer";
import Translation from "@Translation/components/Translation";
import {useTranslation} from "@Translation/hooks/useTranslation";
import ColoredRatingStar from "@Features/ratings/components/ColoredRatingStar";
import ReviewType from "@Pages/reviews/types/Review.type";
import ReviewStation from "@Pages/reviews/components/ReviewStation";
import ReviewText from "@Pages/reviews/components/ReviewText";
import ReviewStationType from "@Pages/reviews/types/ReviewStation.type";
import ReviewCompany from "@Pages/reviews/components/ReviewCompany";
import Row from "@Containers/components/Row";
import IconCheckmarkVerifiedReview from "@Icons/IconCheckmarkVerifiedReview";

interface ReviewProps {
    stationHandler: (station: ReviewStationType) => void,
    activeStationHandler: (stations: string[]) => void,
    filteredStations: string[]
    review: ReviewType,
    companyHandler: (company: string) => void
}

function Review({stationHandler, activeStationHandler, filteredStations, review, companyHandler}: ReviewProps) {
    const by = useTranslation('c_profile.by')
    const {
        anonymous,
        company_ratings: {
            driver_score: driverScoreLabel,
            tidiness_score: tidinessScoreLabel,
            value_for_money_score: valueForMoneyLabel,
            comfortable_ride_score: comfortableRideScoreLabel,
        },
    } = useTranslation('place_review')

    const {
        verified,
        username,
        overallScore,
        ratingQuestions,
        title,
        positives,
        negatives,
        departureDate,
        departureId,
        destinationId,
        departure: departureStation,
        destination: destinationStation,
        companyName,
        datetime,
        isStationReview,
        stopFullName,
    } = review

    const usernameLabel = `${by} ${username || anonymous} | `
    const date = formatDate(datetime, "dd MMMM")
    const roundOverallScore = Math.round(overallScore * 100) / 100

    return (
        <div className="gb--card-default gb--review-snippet gb--review-card">
            {intToBool(verified) && (
                <span className="gb--badge gb--badge-secondary-brand-color">
                    <IconCheckmarkVerifiedReview/>
                    <b>
                        <Translation translationKey="c_profile.verified_review"/>
                    </b>
                </span>
            )}

            {title && (
                <div className="gb--review-title gb--emphasize gb--emphasize-default">
                    {title}
                </div>
            )}

            <div className="gb--review-date-and-score gb--row gb--row-middle gb--row-justify gb--row-wrap">
                <Row>
                    <ColoredRatingStar
                        score={roundOverallScore}
                    />
                    <span className="gb--review-user">
                        {usernameLabel}
                        {' '}
                        {date}
                    </span>
                </Row>
                <span className="gb--row gb--row-middle">
                    {isStationReview == 0 && (
                        <>
                            <ReviewCompany handleCompanyClick={companyHandler}>
                                {companyName}
                            </ReviewCompany>
                            <div className={"gb--review-dot"}></div>
                                <span className="gb--review-stations">
                                <ReviewStation
                                    id={departureId}
                                    name={departureStation}
                                    stationHandler={stationHandler}
                                    activeStationHandler={activeStationHandler}
                                    filteredStations={filteredStations}
                                >
                                    {departureStation}
                                </ReviewStation>
                                {` - `}
                                <ReviewStation
                                    id={destinationId}
                                    name={destinationStation}
                                    stationHandler={stationHandler}
                                    activeStationHandler={activeStationHandler}
                                    filteredStations={filteredStations}
                                >
                                    {destinationStation}
                                </ReviewStation>
                            </span>
                        </>
                    )}
                    {isStationReview == 1 && (
                        <ReviewStation
                            id={departureId}
                            name={departureStation}
                            stationHandler={stationHandler}
                            activeStationHandler={activeStationHandler}
                            filteredStations={filteredStations}
                        >
                            {stopFullName}
                        </ReviewStation>
                    )}
                </span>
            </div>
            <div className="gb--review-text">
                <span className="gb--row gb--row-middle gb--row-wrap gb--emphasize-default">
                    {Object.keys(ratingQuestions).map((questionId) => (
                            <SubScore
                                key={questionId}
                                label={ratingQuestions[questionId][0]}
                                score={ratingQuestions[questionId][1]}
                            />

                    ))}
                </span>
                <ExpandableContainer>
                    <>
                        {!!positives && (
                            <ReviewText>
                                <>
                                    <IconThumb/>
                                    {positives}
                                </>
                            </ReviewText>
                        )}
                        {!!negatives && (
                            <ReviewText>
                                <>
                                    <IconThumb thumbOrientation="down"/>
                                    {negatives}
                                </>
                            </ReviewText>
                        )}
                    </>
                </ExpandableContainer>
            </div>
        </div>
    );
}

export default Review;